// Componente de animación de carga para botones y elementos UI
// Proporciona indicadores visuales de estado de carga consistentes

import React from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

// Variantes de tamaño para el spinner
const SIZES = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8',
};

// Variantes de color para el spinner
const COLORS = {
  primary: 'text-blue-600',
  white: 'text-white',
  gray: 'text-gray-600',
  dark: 'text-gray-900',
};

/**
 * Componente LoadingSpinner
 * 
 * Muestra una animación de rotación para indicar estado de carga
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.size - Tamaño del spinner ('sm', 'md', 'lg', 'xl')
 * @param {string} props.color - Color del spinner ('primary', 'white', 'gray', 'dark')
 * @param {string} props.className - Clases CSS adicionales
 * @param {string} props.label - Texto descriptivo para accesibilidad
 */
const LoadingSpinner = ({ 
  size = 'md', 
  color = 'primary', 
  className = '', 
  label = 'Cargando...' 
}) => {
  const sizeClass = SIZES[size] || SIZES.md;
  const colorClass = COLORS[color] || COLORS.primary;

  return (
    <div 
      className={`inline-flex items-center justify-center ${className}`}
      role="status"
      aria-label={label}
    >
      <AiOutlineLoading3Quarters 
        className={`animate-spin ${sizeClass} ${colorClass}`}
      />
      <span className="sr-only">{label}</span>
    </div>
  );
};

/**
 * Componente LoadingButton
 * 
 * Botón con animación de carga integrada
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.loading - Estado de carga
 * @param {boolean} props.disabled - Estado deshabilitado
 * @param {React.ReactNode} props.children - Contenido del botón
 * @param {string} props.loadingText - Texto durante la carga
 * @param {string} props.size - Tamaño del spinner
 * @param {string} props.spinnerColor - Color del spinner
 * @param {string} props.className - Clases CSS adicionales
 * @param {Object} props.rest - Propiedades adicionales del botón
 */
export const LoadingButton = ({ 
  loading = false, 
  disabled = false, 
  children, 
  loadingText = 'Cargando...', 
  size = 'md',
  spinnerColor = 'white',
  className = '',
  ...rest 
}) => {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`
        border 
        border-red-800 
        text-red-800 
        hover:bg-red-600 
        hover:text-white
        flex 
        justify-center
        items-center 
        transition-colors 
        duration-200
        inline-flex 
        p-2
        w-full
        text-sm 
        font-medium 
        rounded-lg
        transition-colors 
        duration-200
        focus:outline-none 
        focus:ring-2 
        focus:ring-offset-2
        disabled:opacity-50 
        disabled:cursor-not-allowed
        ${loading ? 'cursor-wait' : ''}
        ${className}
      `}
    >
      {loading && (
        <>
          <LoadingSpinner 
            size={size} 
            color={spinnerColor} 
            className="mr-2" 
          />
          {loadingText}
        </>
      )}
      {!loading && children}
    </button>
  );
};

export { LoadingSpinner };
export default LoadingSpinner;
